import React, { useContext, useEffect, useState } from "react";
import {Box, makeStyles, MenuItem, MenuList, Divider, Grid, GridList, GridListTile, Typography} from "@material-ui/core";
import PropTypes from "prop-types";
import clsx from "clsx";
import SearchResortPaper from "../../widget/paper/SearchResortPaper";
import {useBreakpoints} from "../../../hooks/useBreakpoints";
import { useI18N } from "../../../common/common";
import { ResortsContext } from "../../../store/providers/ResortsProvider";
import { LocaleContext } from "../../../store/providers/LocaleProvider";
import { getGroupedByCountry, transformDestinations } from "../../../selectors/resorts";

const useStyles = makeStyles((theme) => ({
    root: {
        overflowX: "hidden",
        overflowY: "auto",
        padding: theme.spacing(2, 0, 0, 0)
    },
    countryList: {
        position: "fixed",
        padding: 0
    },
    countryListItem: {
        "&:hover": {
            backgroundColor: theme.palette.primary["50"]
        },
        "&.selected": {
            borderLeft: "5px solid " + theme.palette.secondary["700"],
            "& > p": {
                marginLeft: "-5px",
            }
        }
    },
    resortTile: {
        height: "100% !important",
        padding: "0!important"
    },
}));

const ResortSelection = ({resortId, onResortSelected}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const {isExtraSmallScreen, isSmallScreen, isSmallScreenVertical} = useBreakpoints();

    const {localeUppercaseUnderscore} = useContext(LocaleContext)[0];
    const [resortsStateAndIds, resortsDispatch] = useContext(ResortsContext);
    const {resortsLoading, nonPartnerResorts, partnerResorts} = resortsStateAndIds;

    const [country, setCountry] = useState(i18n("view.booking_information.partner_resorts"));
    const [resortsGroupedByCountry, setResortsGroupedByCountry] = useState();
    const [selectedResortId, setSelectedResortId] = useState(resortId);

    useEffect(() => {
        if (partnerResorts && !partnerResorts.length && nonPartnerResorts && !nonPartnerResorts.length && !resortsLoading) {
            resortsDispatch({type: "GET_RESORTS_INIT", payload: {locale: localeUppercaseUnderscore, is_bookable: true}});
        } else if (!resortsLoading) {
            const partnerResortsGroupedByCountry = getGroupedByCountry(transformDestinations(partnerResorts));
            const allResortsGroupedByCountry = getGroupedByCountry(transformDestinations([...nonPartnerResorts, ...partnerResorts]));

            setResortsGroupedByCountry({
                [i18n("view.booking_information.partner_resorts")]: Object.keys(partnerResortsGroupedByCountry)?.map(item => partnerResortsGroupedByCountry[item]).flat(),
                ...allResortsGroupedByCountry
            });
        }
    }, [partnerResorts, nonPartnerResorts]);

    const handleScroll = () => {
        const scrollableContainer = document.getElementById("resort_select_scrollable_container");

        Object.keys(resortsGroupedByCountry).every(country => {
            const item = document.getElementById("country_" + country);
            const rect = item.getBoundingClientRect();
            const containerRect = scrollableContainer.getBoundingClientRect();

            // If the item's top is within the visible area of the container, it is considered the top element
            if (rect.top + item.clientHeight - 1 >= containerRect.top && rect.top + item.clientHeight - 1 <= containerRect.top + scrollableContainer.clientHeight) {
                setCountry(country);
                return false;
            } else {
                return true;
            }
        });
    };

    const handleCountryClick = (item)=> {
        setCountry(item);
        document.getElementById("country_" + item).scrollIntoView();
    };

    const handleResortClick = (resortSlug) => {
        setSelectedResortId(resortSlug);
        onResortSelected(resortSlug);
    };

    return (
        <Grid
            container
            className={classes.root}
            onScroll={handleScroll}
            id="resort_select_scrollable_container"
        >
            {!isExtraSmallScreen() && !isSmallScreenVertical() && (
                <Grid
                    item
                    xs={4}
                    sm={4}
                    md={4}
                    lg={4}
                >
                    <MenuList
                        className={classes.countryList}
                    >
                        {resortsGroupedByCountry && Object.keys(resortsGroupedByCountry)?.map((mCountry, index) => {
                            return(
                                <MenuItem
                                    dense
                                    className={clsx(classes.countryListItem, {["selected"]: mCountry === country})}
                                    onClick={() => handleCountryClick(mCountry, index)}
                                    key={index}
                                >
                                    <Typography
                                        variant="body2"
                                    >
                                        {mCountry}
                                    </Typography>
                                </MenuItem>
                            );
                        })}
                    </MenuList>
                </Grid>
            )}

            <Grid
                item
                xs={12}
                sm={isSmallScreenVertical() ? 12 : 8}
            >
                {resortsGroupedByCountry && Object.keys(resortsGroupedByCountry)?.map((country, index, array) => {
                    return (
                        <Box
                            key={country}
                            id={"country_" + country}
                            pt={1}
                        >
                            <Box
                                mb={2}
                            >
                                <Typography
                                    variant="h5"
                                >
                                    {country}
                                </Typography>
                            </Box>

                            <Grid
                                item
                                xs={12}
                            >
                                <GridList
                                    cols={(isExtraSmallScreen() || isSmallScreen()) ? 1 : 2}
                                >
                                    {
                                        Object.values(resortsGroupedByCountry[country]).map((resortsDestination, index) => {
                                            let destinationSubtitle = !resortsDestination?.resorts?.[0]?.destination?.hideAsDestinationSubtitle ? (resortsDestination?.resorts?.[0]?.destination?.destination || "") : null;
                                            if (resortsDestination?.isPartner) {
                                                destinationSubtitle = (destinationSubtitle ? destinationSubtitle + ", " : "") + resortsDestination?.country;
                                            }

                                            return (
                                                <GridListTile
                                                    key={index}
                                                    xs={12}
                                                    className={classes.resortTile}
                                                >
                                                    <SearchResortPaper
                                                        title={resortsDestination?.destination}
                                                        subtitle={destinationSubtitle}
                                                        imageUrl={resortsDestination?.resorts?.[0]?.resortDestinationImagesCollection?.items?.[0]?.url}
                                                        onClick={() => handleResortClick(resortsDestination?.resorts?.[0]?.hapimagResortId)}
                                                        selected={resortsDestination?.resorts?.[0]?.hapimagResortId === selectedResortId}
                                                    />
                                                </GridListTile>
                                            );
                                        })
                                    }
                                </GridList>
                            </Grid>

                            {index < array.length - 1 && (
                                <Box
                                    mt={1}
                                    mb={1}
                                    pr={isExtraSmallScreen() ? 0 : 3}
                                >
                                    <Divider />
                                </Box>
                            )}

                        </Box>
                    );
                })}
            </Grid>
        </Grid>
    );
};

ResortSelection.defaultProps = {};

ResortSelection.propTypes = {
    resortId: PropTypes.number,
    onResortSelected: PropTypes.func,
};

export default ResortSelection;
